import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'github-markdown-css';

// Component that gets rendered as a popup. This contains a MarkUp content and renders that for guiding users how to tailor a query for the search.
const QueryGuideModal = ({ isOpen, onClose }) => {
    const guideContent = `
# Search Query Guide
## Basic Search Operators

| Operator | Description | Example |
|----------|-------------|---------|
| \`&\` | AND operator | \`camera & lens\` |
| \`/\` | OR operator | \`camera / sensor\` |
| \`!\` | NOT operator | \`!camera\` |
| \`()\` | Grouping | \`(camera & lens) / sensor\` |
| \`"..."\` | Exact phrase | \`"artificial intelligence"\` |
| \`term|\` | Exact word | \`camera|\` |

## Search Examples

### Basic Search
* Simple word: \`camera\`
* Exact word: \`camera|\`
* AND: \`camera & lens\`
* OR: \`camera / sensor\`
* NOT: \`!camera\`
* Phrase: \`"image processing"\`
* Grouping: \`(camera & lens) / sensor\`
* Mixed: \`camera & lens / sensor\`

### Complex Search
* Nested: \`(camera & lens) / (sensor & chip)\`
* Mixed: \`"deep learning" & neural| / (AI & !basic)\`
* Groups: \`(camera & (lens / sensor)) & !mobile\`

## Advanced Examples

### Combinations
\`\`\`
(processor| & memory|) / ("neural network" & !basic)
\`\`\`
- Finds documents with exact words "processor" AND "memory"
- OR documents with phrase "neural network" but NOT "basic"

### Nested Groups
\`\`\`
(camera & (lens| / sensor|)) & !"mobile phone"
\`\`\`
- Finds documents with "camera" AND either exact word "lens" OR "sensor"
- Excludes documents containing the phrase "mobile phone"

## Search Modes
* **Normal Mode**: Searches in document content and titles
* **Deep Search Mode**: Additionally includes search strategies (Tick the chekbox \`Search Across Strings\`)

## Tips
1. Use \`|\` for exact words to avoid partial matches (e.g., \`camera|\`)
2. Use quotes for multi-word phrases (e.g., \`"artificial intelligence"\`)
3. Group related terms with parentheses (e.g., \`(camera & lens) / sensor\`)
4. Combine different operators for precise searching (e.g., \`"deep learning" & neural| / (AI & !basic)\`)`;

if (!isOpen) return null;

return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 z-50 flex justify-center items-center">
        <div className="bg-white dark:bg-gray-900 rounded-xl w-11/12 max-w-7xl max-h-[90vh] overflow-y-auto p-6 relative">
            <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-xl font-bold p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all"
            >
                ✕
            </button>
            <div className="markdown-body prose dark:prose-invert prose-sm sm:prose lg:prose-lg xl:prose-xl max-w-none dark:text-gray-200">
                <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                        code: ({node, inline, className, children, ...props}) => (
                            <code className={`${className} bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded-lg px-2 py-1`} {...props}>
                                {children}
                            </code>
                        ),
                        table: ({node, className, children, ...props}) => (
                            <table className="min-w-full border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800" {...props}>
                                {children}
                            </table>
                        ),
                        th: ({node, className, children, ...props}) => (
                            <th className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2 text-left text-gray-800 dark:text-gray-200" {...props}>
                                {children}
                            </th>
                        ),
                        td: ({node, className, children, ...props}) => (
                            <td className="border-b border-gray-200 dark:border-gray-700 p-2 text-gray-600 dark:text-gray-300" {...props}>
                                {children}
                            </td>
                        ),
                        div: ({node, className, children, ...props}) => (
                            <div className={`${className} prose-headings:mt-0`} {...props}>
                                {children}
                            </div>
                        )
                    }}
                >
                    {guideContent}
                </ReactMarkdown>
            </div>
        </div>
    </div>
);
};

export default QueryGuideModal;